/**
 * Credit Balances List Page
 * HTMX-driven filtering & pagination.
 * Retains SearchPicker for customer autocomplete.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';

let customerPicker = null;

/**
 * Initialize the credit balances list page
 */
export function initCreditBalancesList(options = {}) {
  const { hasActiveFilters = false, urls = {} } = options;

  initFilterPanelUI(hasActiveFilters);
  initCustomerPicker(urls);
}

/**
 * Filter panel toggle / reset (HTMX handles requests)
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('filterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  if (toggleBtn && panel && !toggleBtn._cbFilterBound) {
    toggleBtn._cbFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._cbResetBound) {
    resetBtn._cbResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });

      form.querySelectorAll('input[type="hidden"]').forEach((inp) => {
        inp.value = '';
      });

      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });

      if (customerPicker) customerPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/**
 * Initialize customer search picker
 */
function initCustomerPicker(urls) {
  const searchInput = document.getElementById('customer-search');
  if (!searchInput) return;

  customerPicker = new SearchPicker({
    inputId: 'customer-search',
    hiddenInputId: 'customer-filter',
    dropdownId: 'customer-search-results',
    endpoint: (urls.customerSearch || '/office/api/customers/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
    isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
    onSelect: () => {
      const form = document.getElementById('filterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('filterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

// renderCustomerItem moved to ``frontend/src/utils/ui/customer-picker.js``.

let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', (e) => {
    const target = e.detail && e.detail.target;
    const id = target && target.id;
    if (id !== 'list-results' && id !== 'main-content') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="creditBalancesList"``).
 */
export function autoInitCreditBalancesList() {
  const root = document.querySelector('[data-auto-init="creditBalancesList"]');
  if (!root) return;
  initCreditBalancesList({
    hasActiveFilters: root.dataset.hasActiveFilters === '1',
    urls: {
      customerSearch: root.dataset.customerSearchUrl || '/office/api/customers/search/',
    },
  });
  bindAfterSettleOnce();
}

window.initCreditBalancesList = initCreditBalancesList;
